<template>
  <div class="feedback-list" :class="viewMode === 'board' ? 'flex flex-col h-[calc(100vh-57px)] overflow-hidden px-0 py-0' : 'px-6 py-6'">
    <div class="feedback-list-header flex items-center gap-4 flex-shrink-0" :class="viewMode === 'board' ? 'px-6 pt-4 pb-2' : ''">
      <h2 class="text-lg font-semibold text-gray-900">Feedback</h2>

      <!-- View toggle -->
      <div class="flex items-center bg-gray-100 rounded-md p-0.5">
        <button
          @click="setViewMode('list')"
          class="px-2.5 py-1 text-xs rounded transition-colors"
          :class="viewMode === 'list'
            ? 'bg-white text-gray-900 shadow-sm font-medium'
            : 'text-gray-500 hover:text-gray-700'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          List
        </button>
        <button
          @click="setViewMode('board')"
          class="px-2.5 py-1 text-xs rounded transition-colors"
          :class="viewMode === 'board'
            ? 'bg-white text-gray-900 shadow-sm font-medium'
            : 'text-gray-500 hover:text-gray-700'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
          Board
        </button>
      </div>

      <!-- Filters -->
      <select
        v-model="filterProduct"
        @change="syncFiltersToUrl"
        class="text-sm border border-gray-300 rounded-md px-2 py-1"
      >
        <option :value="undefined">All Products</option>
        <option
          v-for="product in products"
          :key="product.id"
          :value="product.id"
        >
          {{ product.displayName }}
        </option>
      </select>

      <select
        v-if="viewMode === 'list'"
        v-model="filterStatus"
        @change="syncFiltersToUrl"
        class="text-sm border border-gray-300 rounded-md px-2 py-1"
      >
        <option :value="undefined">All Statuses</option>
        <option value="new">New</option>
        <option value="investigating">Investigating</option>
        <option value="planned">Planned</option>
        <option value="in_progress">In Progress</option>
        <option value="resolved">Resolved</option>
        <option value="archived">Archived</option>
      </select>

      <div class="flex-1"></div>

      <!-- Search -->
      <div v-if="viewMode === 'list'" class="flex items-center gap-2">
        <input
          v-model="searchText"
          @keyup.enter="performSearch"
          placeholder="Search feedback..."
          class="text-sm border border-gray-300 rounded-md px-3 py-1"
        />
        <button
          v-if="searchText.trim()"
          @click="clearSearch"
          class="text-xs text-gray-400 hover:text-gray-600"
        >
          Clear
        </button>
      </div>

      <span class="text-xs text-gray-400" v-if="displayItems">
        {{ displayItems.length }} items
      </span>
    </div>

    <!-- Bulk Action Bar -->
    <div
      v-if="selectedIds.size > 0"
      class="feedback-list-bulk-bar sticky top-0 z-10 flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2.5 mt-4"
    >
      <span class="text-sm font-medium text-indigo-700">
        {{ selectedIds.size }} selected
      </span>
      <div class="flex-1"></div>

      <select
        v-model="bulkStatus"
        class="text-sm border border-indigo-300 rounded-md px-2 py-1 bg-white"
      >
        <option value="" disabled>Change status...</option>
        <option value="new">New</option>
        <option value="investigating">Investigating</option>
        <option value="planned">Planned</option>
        <option value="in_progress">In Progress</option>
        <option value="resolved">Resolved</option>
        <option value="archived">Archived</option>
        <option value="wont_fix">Won't Fix</option>
      </select>
      <button
        @click="executeBulkStatusChange"
        :disabled="!bulkStatus || bulkActionPending"
        class="text-sm px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
      >
        Apply
      </button>

      <span class="text-gray-300">|</span>

      <select
        v-model="bulkAssignee"
        class="text-sm border border-indigo-300 rounded-md px-2 py-1 bg-white"
      >
        <option value="" disabled>Assign to...</option>
        <option
          v-for="member in teamMembers"
          :key="member.userId"
          :value="member.userId"
        >
          {{ member.name }}
        </option>
      </select>
      <button
        @click="executeBulkAssign"
        :disabled="!bulkAssignee || bulkActionPending"
        class="text-sm px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
      >
        Assign
      </button>

      <button
        @click="clearSelection"
        class="text-sm text-gray-500 hover:text-gray-700 ml-2"
      >
        Clear
      </button>
    </div>

    <!-- Board View -->
    <FeedbackBoard
      v-if="viewMode === 'board' && displayItems"
      :items="displayItems"
      @statusChanged="fetchFeedback"
      class="flex-1 min-h-0"
    />

    <!-- List View -->
    <div v-if="viewMode === 'list' && displayItems" class="feedback-list-items flex flex-col gap-2 pt-4">
      <!-- Select all -->
      <div class="flex items-center gap-2 px-1 pb-1" v-if="displayItems.length > 0">
        <input
          type="checkbox"
          :checked="allSelected"
          :indeterminate="someSelected && !allSelected"
          @change="toggleSelectAll"
          class="rounded border-gray-300 text-indigo-600"
        />
        <span class="text-xs text-gray-400">Select all</span>
      </div>

      <div
        v-for="item in displayItems"
        :key="item.id"
        class="feedback-list-item flex items-start gap-3 bg-white rounded-lg border border-gray-200 px-4 py-3 hover:border-indigo-300 transition-colors"
        :class="{ 'border-indigo-400 bg-indigo-50/30': selectedIds.has(item.id) }"
      >
        <!-- Checkbox -->
        <input
          type="checkbox"
          :checked="selectedIds.has(item.id)"
          @change="toggleSelect(item.id)"
          @click.stop
          class="mt-1.5 rounded border-gray-300 text-indigo-600 flex-shrink-0"
        />

        <!-- Clickable content -->
        <div
          @click="uiStore.viewFeedbackDetail(item.id)"
          class="flex items-start gap-4 flex-1 cursor-pointer min-w-0"
        >
          <!-- Priority Badge -->
          <span
            class="inline-flex items-center justify-center w-9 h-9 rounded-full text-xs font-bold text-white flex-shrink-0"
            :class="getPriorityColor(item.priorityScore)"
          >
            {{ item.priorityScore }}
          </span>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <p class="text-sm text-gray-900">
              {{ item.aiSummary || truncate(item.transcript, 120) }}
            </p>
            <div class="flex items-center gap-3 pt-1">
              <span class="text-xs text-gray-500">{{ item.userEmail }}</span>
              <span v-if="item.audioUrl" class="text-xs text-gray-400" title="Has audio">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m-4-1h8" />
                </svg>
              </span>
              <span v-if="item.screenshotUrls && item.screenshotUrls.length > 0" class="text-xs text-gray-400" title="Has screenshots">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
              <span class="text-xs text-gray-400">{{ formatDate(item.createdAt) }}</span>
            </div>
          </div>

          <!-- Metadata -->
          <div class="flex flex-col items-end gap-1 flex-shrink-0">
            <span
              v-if="item.type"
              class="text-xs px-2 py-0.5 rounded-full"
              :class="getTypeClass(item.type)"
            >
              {{ formatType(item.type) }}
            </span>
            <span
              class="text-xs px-2 py-0.5 rounded-full"
              :class="getStatusClass(item.status)"
            >
              {{ item.status }}
            </span>
            <span
              v-if="item.sentiment"
              class="text-xs"
              :class="{
                'text-red-500': item.sentiment === 'frustrated',
                'text-gray-400': item.sentiment === 'neutral',
                'text-green-500': item.sentiment === 'positive',
              }"
            >
              {{ getSentimentIcon(item.sentiment) }}
            </span>
          </div>
        </div>
      </div>

      <p v-if="displayItems.length === 0" class="text-sm text-gray-400 text-center py-8">
        No feedback items found
      </p>
    </div>

    <div v-if="viewMode === 'list' && !displayItems && !isSearching" class="pt-6 text-sm text-gray-500">Loading feedback...</div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import apiClient from '../../api/client'
import { useUIStore } from '../../stores/uiStore'
import FeedbackBoard from './FeedbackBoard.vue'

const uiStore = useUIStore()

const viewMode = ref<'list' | 'board'>('list')
const filterProduct = ref<number | undefined>(undefined)
const filterStatus = ref<string | undefined>(undefined)
const searchText = ref('')
const isSearching = ref(false)

function setViewMode(mode: 'list' | 'board') {
  viewMode.value = mode
  syncFiltersToUrl()
  fetchFeedback()
}

// Data
const feedbackItems = ref<any[] | null>(null)
const searchResults = ref<any[]>([])
const products = ref<any[]>([])
const teamMembers = ref<any[]>([])

// Bulk action state
const selectedIds = ref<Set<number>>(new Set())
const bulkStatus = ref('')
const bulkAssignee = ref('')
const bulkActionPending = ref(false)

// Read filters from URL on mount
onMounted(async () => {
  const params = new URLSearchParams(window.location.search)
  if (params.get('view') === 'board') viewMode.value = 'board'
  if (params.get('product')) filterProduct.value = Number(params.get('product'))
  if (params.get('status')) filterStatus.value = params.get('status')!
  if (params.get('q')) {
    searchText.value = params.get('q')!
    isSearching.value = true
  }

  // Fetch initial data
  const [productsData, teamData] = await Promise.all([
    apiClient.products.list(),
    apiClient.teamMembers.list(),
  ])
  products.value = productsData
  teamMembers.value = teamData

  await fetchFeedback()
})

async function fetchFeedback() {
  if (isSearching.value && searchText.value.trim()) {
    searchResults.value = await apiClient.feedback.search(searchText.value.trim(), {
      productId: filterProduct.value,
    })
  } else {
    feedbackItems.value = await apiClient.feedback.list({
      productId: filterProduct.value,
      status: viewMode.value === 'board' ? undefined : filterStatus.value,
      limit: viewMode.value === 'board' ? 200 : 50,
    })
  }
}

// Refetch when filters change
watch([filterProduct, filterStatus], () => {
  if (!isSearching.value) fetchFeedback()
})

function syncFiltersToUrl() {
  const params = new URLSearchParams()
  if (viewMode.value === 'board') params.set('view', 'board')
  if (filterProduct.value) params.set('product', String(filterProduct.value))
  if (filterStatus.value && viewMode.value === 'list') params.set('status', filterStatus.value)
  if (isSearching.value && searchText.value.trim()) params.set('q', searchText.value.trim())
  const qs = params.toString()
  const newUrl = window.location.pathname + (qs ? '?' + qs : '')
  history.replaceState(null, '', newUrl)
}

const displayItems = computed(() => {
  if (isSearching.value && searchText.value.trim()) {
    return searchResults.value
  }
  return feedbackItems.value ?? []
})

const allSelected = computed(() => {
  if (!displayItems.value || displayItems.value.length === 0) return false
  return displayItems.value.every((item: any) => selectedIds.value.has(item.id))
})

const someSelected = computed(() => {
  if (!displayItems.value) return false
  return displayItems.value.some((item: any) => selectedIds.value.has(item.id))
})

function toggleSelect(id: number) {
  const s = new Set(selectedIds.value)
  if (s.has(id)) s.delete(id)
  else s.add(id)
  selectedIds.value = s
}

function toggleSelectAll() {
  if (allSelected.value) {
    selectedIds.value = new Set()
  } else {
    selectedIds.value = new Set(displayItems.value.map((item: any) => item.id))
  }
}

function clearSelection() {
  selectedIds.value = new Set()
  bulkStatus.value = ''
  bulkAssignee.value = ''
}

async function executeBulkStatusChange() {
  if (!bulkStatus.value || selectedIds.value.size === 0) return
  bulkActionPending.value = true
  try {
    await apiClient.feedback.bulkUpdateStatus({
      feedbackIds: Array.from(selectedIds.value),
      status: bulkStatus.value,
      actorUserId: 'admin-001',
      actorName: 'Admin',
    })
    uiStore.showNotification(`Updated ${selectedIds.value.size} items to "${bulkStatus.value}"`, 'success')
    clearSelection()
    await fetchFeedback()
  } catch (err: any) {
    uiStore.showNotification('Bulk update failed: ' + (err.message || 'Unknown error'), 'error')
  }
  bulkActionPending.value = false
}

async function executeBulkAssign() {
  if (!bulkAssignee.value || selectedIds.value.size === 0) return
  bulkActionPending.value = true
  try {
    await apiClient.feedback.bulkAssign({
      feedbackIds: Array.from(selectedIds.value),
      assigneeId: bulkAssignee.value,
      actorUserId: 'admin-001',
      actorName: 'Admin',
    })
    uiStore.showNotification(`Assigned ${selectedIds.value.size} items`, 'success')
    clearSelection()
    await fetchFeedback()
  } catch (err: any) {
    uiStore.showNotification('Bulk assign failed: ' + (err.message || 'Unknown error'), 'error')
  }
  bulkActionPending.value = false
}

function performSearch() {
  if (searchText.value.trim()) {
    isSearching.value = true
    syncFiltersToUrl()
    fetchFeedback()
  }
}

function clearSearch() {
  searchText.value = ''
  isSearching.value = false
  syncFiltersToUrl()
  fetchFeedback()
}

function getPriorityColor(score: number): string {
  if (score >= 80) return 'bg-red-600'
  if (score >= 60) return 'bg-orange-500'
  if (score >= 40) return 'bg-yellow-500'
  if (score >= 20) return 'bg-blue-500'
  return 'bg-gray-400'
}

function getStatusClass(status: string): string {
  const classes: Record<string, string> = {
    new: 'bg-blue-100 text-blue-700',
    investigating: 'bg-yellow-100 text-yellow-700',
    planned: 'bg-purple-100 text-purple-700',
    in_progress: 'bg-indigo-100 text-indigo-700',
    resolved: 'bg-green-100 text-green-700',
    archived: 'bg-gray-100 text-gray-600',
    wont_fix: 'bg-gray-100 text-gray-600',
  }
  return classes[status] || 'bg-gray-100 text-gray-600'
}

function getTypeClass(type: string): string {
  const classes: Record<string, string> = {
    bug: 'bg-red-100 text-red-700',
    feature_request: 'bg-indigo-100 text-indigo-700',
    improvement: 'bg-teal-100 text-teal-700',
    support: 'bg-amber-100 text-amber-700',
    praise: 'bg-green-100 text-green-700',
    confusion: 'bg-orange-100 text-orange-700',
  }
  return classes[type] || 'bg-gray-100 text-gray-600'
}

function formatType(type: string): string {
  return type.replace(/_/g, ' ')
}

function getSentimentIcon(sentiment: string): string {
  if (sentiment === 'frustrated') return 'Frustrated'
  if (sentiment === 'positive') return 'Positive'
  return 'Neutral'
}

function truncate(text: string, maxLength: number): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const hours = Math.floor(diff / 3600000)
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}
</script>
